import React, { useState, useRef, ChangeEvent, KeyboardEvent, ClipboardEvent, useEffect } from 'react';
import { TextField, Chip, Box, Stack, TextFieldProps } from '@mui/material';

interface ChipInputProps {
  initialChips?: string[];
  onChipsChange?: (chips: string[]) => void;
  placeholder?: string;
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'onKeyDown' | 'onPaste' | 'placeholder'>;
}

const ChipInput: React.FC<ChipInputProps> = ({
  initialChips = [],
  onChipsChange,
  placeholder = '输入标签，按回车添加...',
  textFieldProps = {},
}) => {
  const [chips, setChips] = useState<string[]>(initialChips);
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // 拖拽状态
  const dragState = useRef({
    isDragging: false,
    startIndex: -1,
    targetIndex: -1,
    cloneElement: null as HTMLDivElement | null,
  });

  // 更新 chips 并通知父组件
  const updateChips = (newChips: string[]): void => {
    setChips(newChips);
    onChipsChange?.(newChips);
  };

  // 添加 Chip
  const addChip = (text: string): void => {
    const trimmed = text.trim();
    if (trimmed === '') return;
    if (chips.includes(trimmed)) return;
    updateChips([...chips, trimmed]);
    setInputValue('');
  };

  // 删除 Chip
  const deleteChip = (chipToDelete: string): void => {
    const newChips = chips.filter((chip) => chip !== chipToDelete);
    updateChips(newChips);
  };

  // 移动元素：将 fromIndex 处的元素移动到 toIndex
  const moveChip = (fromIndex: number, toIndex: number): void => {
    if (fromIndex === toIndex) return;
    const newChips = [...chips];
    const [moved] = newChips.splice(fromIndex, 1);
    newChips.splice(toIndex, 0, moved);
    updateChips(newChips);
  };

  // 创建拖拽克隆体
  const createCloneElement = (chipText: string, clientX: number, clientY: number): HTMLDivElement => {
    const clone = document.createElement('div');
    clone.textContent = chipText;
    clone.style.position = 'fixed';
    clone.style.left = `${clientX}px`;
    clone.style.top = `${clientY}px`;
    clone.style.transform = 'translate(-50%, -50%)';
    clone.style.backgroundColor = 'rgba(25, 118, 210, 0.9)';
    clone.style.color = 'white';
    clone.style.borderRadius = '16px';
    clone.style.padding = '4px 12px';
    clone.style.fontSize = '0.875rem';
    clone.style.fontFamily = 'Roboto, sans-serif';
    clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';  // 让鼠标穿透克隆体
    clone.style.whiteSpace = 'nowrap';
    document.body.appendChild(clone);
    return clone;
  };

  // 更新克隆体位置
  const updateClonePosition = (clientX: number, clientY: number): void => {
    if (dragState.current.cloneElement) {
      dragState.current.cloneElement.style.left = `${clientX}px`;
      dragState.current.cloneElement.style.top = `${clientY}px`;
    }
  };

  // 获取鼠标下方 Chip 的索引（基于 data-chip-index 属性）
  const getHoveredChipIndex = (clientX: number, clientY: number): number => {
    const elementsAtPoint = document.elementsFromPoint(clientX, clientY);
    for (let i = 0; i < elementsAtPoint.length; i++) {
      const el = elementsAtPoint[i] as HTMLElement;
      const idxAttr = el.getAttribute('data-chip-index');
      if (idxAttr !== null) {
        const idx = parseInt(idxAttr, 10);
        if (!isNaN(idx)) return idx;
      }
    }
    return -1;
  };

  // 鼠标移动事件：更新克隆体位置，记录目标索引并高亮对应 Chip
  const handleMouseMove = (e: MouseEvent): void => {
    if (!dragState.current.isDragging) return;

    const { clientX, clientY } = e;
    updateClonePosition(clientX, clientY);

    const hoverIndex = getHoveredChipIndex(clientX, clientY);
    if (hoverIndex !== -1 && hoverIndex !== dragState.current.targetIndex) {
      // 移除之前的高亮
      if (dragState.current.targetIndex !== -1) {
        const prevChip = document.querySelector(`[data-chip-index="${dragState.current.targetIndex}"]`) as HTMLElement;
        if (prevChip) prevChip.style.backgroundColor = '';
      }
      // 更新目标索引并高亮新 Chip
      dragState.current.targetIndex = hoverIndex;
      const targetChip = document.querySelector(`[data-chip-index="${hoverIndex}"]`) as HTMLElement;
      if (targetChip) {
        targetChip.style.transition = 'background-color 0.2s';
        targetChip.style.backgroundColor = 'rgba(25, 118, 210, 0.2)';
      }
    }
  };

  // 鼠标释放事件：执行最终移动，清理拖拽资源
  const handleMouseUp = (): void => {
    if (!dragState.current.isDragging) return;

    // 清除高亮
    if (dragState.current.targetIndex !== -1) {
      const targetChip = document.querySelector(`[data-chip-index="${dragState.current.targetIndex}"]`) as HTMLElement;
      if (targetChip) targetChip.style.backgroundColor = '';
    }

    // 如果目标索引有效且不同于起始索引，则移动元素
    if (dragState.current.targetIndex !== -1 && dragState.current.targetIndex !== dragState.current.startIndex) {
      moveChip(dragState.current.startIndex, dragState.current.targetIndex);
    }

    // 移除克隆体
    if (dragState.current.cloneElement) {
      document.body.removeChild(dragState.current.cloneElement);
      dragState.current.cloneElement = null;
    }

    // 移除全局事件监听
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);

    // 重置拖拽状态
    dragState.current = {
      isDragging: false,
      startIndex: -1,
      targetIndex: -1,
      cloneElement: null,
    };
  };

  // 开始拖拽
  const handleChipMouseDown = (e: React.MouseEvent<HTMLDivElement>, index: number): void => {
    if (e.button !== 0) return; // 仅左键
    e.preventDefault();
    e.stopPropagation();

    const { clientX, clientY } = e;
    const chipText = chips[index];
    const clone = createCloneElement(chipText, clientX, clientY);

    dragState.current = {
      isDragging: true,
      startIndex: index,
      targetIndex: index,   // 初始目标索引为自身，避免立即触发高亮清除
      cloneElement: clone,
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // 组件卸载时清理拖拽相关事件和克隆体
  useEffect(() => {
    return () => {
      if (dragState.current.isDragging) {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        if (dragState.current.cloneElement) {
          document.body.removeChild(dragState.current.cloneElement);
        }
      }
    };
  }, []);

  // 键盘事件处理（空格添加、退格删除）
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    console.log(event.key)
    if (event.key === 'Enter') {
      event.preventDefault();
      if (inputValue.trim()) {
        addChip(inputValue);
      }
    } else if (event.key === 'Backspace' && inputValue === '' && chips.length > 0) {
      const newChips = chips.slice(0, -1);
      updateChips(newChips);
    }
  };

  // 粘贴处理（按空格分词）
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const pasteText = event.clipboardData.getData('text/plain');
    const words = pasteText.split(/\s+/).filter((word) => word.trim() !== '');
    if (words.length === 0) return;
    const newChips = [...chips];
    let hasNew = false;
    for (const word of words) {
      if (!newChips.includes(word)) {
        newChips.push(word);
        hasNew = true;
      }
    }
    if (hasNew) updateChips(newChips);
    setInputValue('');
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Chip 列表区域 */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
          userSelect: 'none',
        }}
      >
        {chips?.map((chip, index) => (
          <Chip
            key={`${chip}-${index}`}
            label={chip}
            onDelete={() => deleteChip(chip)}
            variant="outlined"
            color="primary"
            onMouseDown={(e) => handleChipMouseDown(e, index)}
            sx={{
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
            }}
            data-chip-index={index}
          />
        ))}
      </Stack>

      {/* 输入框区域 */}
      <TextField
        inputRef={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={chips.length === 0 ? placeholder : '继续添加...'}
        fullWidth
        variant="outlined"
        size="small"
        {...textFieldProps}
      />
    </Box>
  );
};

export default ChipInput;